import { useEffect } from 'react';
import {
  tintToCssColor,
  tintForeground,
  groundToTint,
  parseColorStrict,
  readThemeAccent,
  CLASSIC_TINT,
} from '../utils/coverTint';

/* The pre-tint palette, read off the commit that introduced the tint
 * (`f2291b830^`): white cards on a white page, told apart by a hairline. */
/** Set on <html> while a shop page is mounted: 'light' when its glyph ink is white. */
export const INK_ATTR = 'data-shop-ink';

const CLASSIC_SURFACE = '#FFFFFF';
const CLASSIC_SURFACE_HOVER = '#FAFBFC';
const CLASSIC_HAIRLINE = '#ECEEF3';
const CLASSIC_SOFT = '#F7F7F7';

/**
 * READ THE GROUND THE PAGE IS ACTUALLY PAINTED, whatever declared it.
 *
 * Not `getComputedStyle(root).getPropertyValue('--shop-tint')`: an unregistered
 * custom property computes to a TOKEN STREAM, not to a colour, so on the
 * fallback path that returns the literal string
 * `color-mix(in srgb, var(--c-primary, #F36523) 14%, #ffffff)` — verified on
 * the live shop. Assigning it to a real colour property and reading THAT back
 * is what forces the engine to substitute the variables and evaluate the
 * `color-mix()`, because `color` resolves to an absolute value at
 * computed-value time.
 *
 * The custom property is tried first all the same, for the case where it
 * already holds an absolute colour — which is every derived tint, and is also
 * the only form jsdom can resolve, so the guard can exercise this at all.
 *
 * Answers `null` rather than a guess when there is nothing to read.
 */
export function resolveGroundColor(root) {
  if (!root || typeof document === 'undefined') return null;
  const direct = parseColorStrict((root.style?.getPropertyValue?.('--shop-tint') || '').trim());
  if (direct) return direct;
  let probe = null;
  try {
    probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.position = 'absolute';
    probe.style.width = '0';
    probe.style.height = '0';
    probe.style.overflow = 'hidden';
    probe.style.color = 'var(--shop-tint)';
    root.appendChild(probe);
    return parseColorStrict(window.getComputedStyle(probe).color);
  } catch (_err) {
    return null;
  } finally {
    if (probe && probe.parentNode) probe.parentNode.removeChild(probe);
  }
}

/**
 * Publishes the page's foreground and glass tokens as CSS custom properties.
 *
 * They go on `document.documentElement`, not on the page wrapper, for one
 * concrete reason: the search drawer, the filter/sort sheets and the side
 * menu all render through a PORTAL onto `document.body` — outside the shop
 * page's own element. Scoping the tint to the wrapper would leave exactly
 * those surfaces on the old white theme, which is the "side menu doesn't
 * match" bug in visual form.
 *
 * ── ONE PATH, WHICH IS THE WHOLE OF THIS FIX ─────────────────────────────
 *
 * The owner: "regarding the font colour, make sure it switches to white if
 * the background is too dark for black."
 *
 * This hook used to open `if (!tint) return undefined;`. With no derived
 * cover tint it wrote NO foreground tokens at all and the page fell through
 * to whatever `Pages/Shop/styles.module.css` declares — a hand-written pair
 * of ground and foreground that never compare themselves to each other.
 * `tintForeground`, the function that picks white or black BY MEASURED
 * CONTRAST and is the reason this page works across the whole tint band, was
 * not consulted on that path at all.
 *
 * Measured on the live shop with the runtime tokens stripped: on ALL NINE
 * themes, in FULL mode, under `html.dark`, the foreground came out at
 * 1.00–1.22:1 against the ground — near-white text on a near-white page,
 * because `html.dark` (specificity 0,1,1) sets a light `--shop-text` while
 * the tint stays the pale theme wash. LIGHT and CLASSIC escaped it only
 * because their `:root[data-shop-bg-mode]` blocks restate a dark foreground
 * by hand; the same patch, applied twice and left off the third mode.
 *
 * So there is no early return any more. When no tint is derived, the GROUND
 * the stylesheet resolved is read back off the document and run through the
 * same `tintForeground`. Derived or fallen back, one function decides.
 *
 * ── AND IT PUBLISHES THE GROUND IT DERIVED FROM. THAT IS THE SECOND FIX ──
 *
 * This hook used to publish every surface on the fallback path and withhold
 * the two tokens that NAME the page: `--shop-tint` and `--shop-page-bg` were
 * gated behind `if (tint)`. Measured on the live shop at 1440, with a real
 * salon and no cover:
 *
 *     --shop-tint      color-mix(in srgb, #F36523 14%, #ffffff)  ← unresolved
 *     --shop-glass-bg  hsla(19, 22%, 76%, 0.85)                  ← published
 *
 * The page is painted hsl(19, 88%, 93.5%) and every pill, circle, card and
 * panel standing on it was published from hsl(19, 22%, 93%). Same hue, a
 * quarter of the chroma — a taupe pill on a peach page, which is the "grey
 * tabs" the owner has reported. Two colour sources on one screen, and no way
 * for them to agree, because one of them was never written down.
 *
 * Two changes end it, and they only work together:
 *
 *   · the ground is read with `groundToTint`, not `rgbToTint`. The latter is
 *     the PHOTOGRAPH sampler's reducer and clamps saturation to
 *     `TINT_MAX_SATURATION`; the stylesheet's ground is a tint already and
 *     needs no reducing. See `groundToTint` in `utils/coverTint.js`.
 *   · the ground is PUBLISHED, derived or fallen back. It round-trips to the
 *     same bytes the stylesheet resolved, so nothing repaints — what changes
 *     is that the page's colour is now written down in the one token every
 *     surface, and every downstream consumer of `--shop-page-bg`, reads.
 *
 * The old reason for withholding it was that an inline value would shadow the
 * `[data-shop-bg-mode]` blocks. It cannot: `publish()` CLEARS first and then
 * reads, so the stylesheet still decides the ground, and the observer below
 * re-runs the whole thing whenever the mode attribute changes. The blocks
 * still govern; they simply govern through a token that is now resolved.
 *
 * It re-derives when the document's own attributes change — `data-theme`,
 * `data-shop-bg-mode`, and the `dark` class — because each of those changes
 * the ground underneath a foreground we have already published.
 *
 * Cleanup removes every property it set, so navigating off the shop does
 * not leak a shop's colour onto the rest of the storefront.
 */
export default function useTintVars(tint) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    let written = [];

    const clear = () => {
      written.forEach((k) => root.style.removeProperty(k));
      written = [];
      root.removeAttribute(INK_ATTR);
    };

    const publish = () => {
      // The ground must be read with nothing of ours on the element, or the
      // fallback path reads back its own last answer.
      clear();
      const ground = tint || (() => {
        const rgb = resolveGroundColor(root);
        return rgb ? groundToTint(rgb) : null;
      })();
      if (!ground) return;

      const fg = tintForeground(ground);

      /* CLASSIC SEPARATES WITH A HAIRLINE, NOT BY GOING DARKER.
       *
       * Every surface here is solved as "the page's own colour moved a
       * measured CIE ΔL*". On a tinted ground that is right and it is what
       * makes a shop feel like itself. On CLASSIC — a WHITE page — there is no
       * headroom above, so every surface separates DOWNWARD and a white page
       * comes out with grey cards and grey buttons. The owner, seeing it:
       * "what with the grey coloring of the buttons? didn't we say it's old?
       * why not white?"
       *
       * The pre-tint design did not solve its surfaces at all. It painted them
       * WHITE and separated them with a hairline — #FFFFFF cards on a #FFFFFF
       * page, told apart by a 1px #ECEEF3 rule, with #F7F7F7 for the soft
       * bands. That is what CLASSIC means, and restoring it is the whole point
       * of the mode.
       *
       * Only the GROUNDS are overridden. Text, muted text, contrast and the
       * selected state are still solved against the page, because those are
       * legibility answers and a hairline does not change what is readable.
       *
       * Detected from the ground rather than passed as a mode: `pageTintForMode`
       * returns CLASSIC_TINT by value for this mode, and a page whose ground
       * really is that colour IS a classic page whatever route it arrived by. */
      const isClassicGround =
        ground
        && ground.l >= 99.5
        && Math.abs((ground.h ?? 0) - CLASSIC_TINT.h) < 0.5
        && Math.abs((ground.s ?? 0) - CLASSIC_TINT.s) < 0.5;

      const classicGrounds = isClassicGround
        ? {
            '--shop-glass-bg': CLASSIC_SURFACE,
            '--shop-glass-bg-hover': CLASSIC_SURFACE_HOVER,
            '--shop-glass-solid': CLASSIC_SURFACE,
            '--shop-glass-border': CLASSIC_HAIRLINE,
            '--shop-card-bg': CLASSIC_SURFACE,
            '--shop-border': CLASSIC_HAIRLINE,
            '--shop-soft-bg': CLASSIC_SOFT,
            /* `--shop-plate` is deliberately NOT published here. It is only
             * ever read with a `#fff` fallback, which is already the right
             * answer on a white page — and publishing it would add a ground
             * that nothing measures muted text against, which the contrast
             * guard correctly refuses. */
          }
        : null;

      const vars = {
        '--shop-text': fg.text,
        '--shop-text-muted': fg.textMuted,
        '--shop-glass-bg': fg.glassBg,
        '--shop-glass-bg-hover': fg.glassBgHover,
        '--shop-glass-border': fg.glassBorder,
        '--shop-glass-highlight': fg.glassHighlight,
        // Derived, not a two-way switch on "is the tint dark": see
        // `shadowAlpha` in utils/coverTint.js. The old pair read on a pale
        // page and vanished on a dark one, which is the same asymmetry the
        // lit top edge had.
        '--shop-glass-shadow': fg.glassShadow,
        // The glass surface with no alpha at all — for the two places a
        // translucent film cannot work: the logo disc and the free-shipping
        // chip, both of which sit ON the cover photo rather than on the page.
        '--shop-glass-solid': fg.glassSolid,
        '--shop-scrim-bg': fg.scrimBg,
        // The plate an uploaded brand mark sits on — always light, because the
        // mark is the owner's raster and we cannot recolour it. See
        // `tintForeground`.
        '--shop-logo-plate': fg.logoPlate,
        '--shop-logo-plate-text': fg.logoPlateText,
        // The brand strip's mat. The strip shows the LOGOS as its items now,
        // with no plate and no circle — so this is `transparent` on every page
        // whose own foreground resolves dark, i.e. every page light enough to
        // carry an uploaded mark, and the light plate only where a dark cover
        // would otherwise swallow a dark-ink PNG. Measured, not switched by
        // hand. See `logoMat` in utils/coverTint.js.
        '--shop-logo-mat': fg.logoMat,
        '--shop-logo-mat-text': fg.logoMatText,
        // The plate for a mark drawn ON THE GLASS — the hero's lockup, the
        // condensed nav's lockup and the side panel's mark. It replaces the
        // page-coloured ring those three used to wear, and it is TWO values
        // for one disc: `-core` is the flat light centre the artwork sits on,
        // `--shop-mark-plate` is the searched rim that stands ΔL*
        // MARK_PLATE_DELTA_L off the pill. The disc ramps from one to the
        // other, so the separation is an area and not an edge — a ring is
        // what the owner has called a border three times.
        // See `markPlateLightness`.
        '--shop-mark-plate': fg.markPlate,
        '--shop-mark-plate-core': fg.markPlateCore,
        '--shop-mark-plate-text': fg.markPlateText,
        // Product cards read as the same raised surface as everything else,
        // so the page has ONE value for "a thing on top of the page".
        '--shop-card-bg': fg.glassBg,
        // Derived, not `l ± a hand-picked number`. The old pair stepped
        // TOWARD the foreground in both directions — lighter under white
        // text, darker under black — so the one surface that exists to be a
        // quiet alternate ground was the worst ground on the page for the
        // muted text painted on it (2.32–2.97:1 in all four measured modes).
        // `softSurfaceLightness` steps away from the text where the page
        // leaves room. See `SOFT_SURFACE_DELTA_L` in utils/coverTint.js.
        '--shop-soft-bg': fg.softBg,
        '--shop-border': fg.glassBorder,
        /* THE TWO SURFACES THAT ARE NOT THE INK.
         *
         * `--shop-text` is a GLYPH colour — `#000000` or `#ffffff`, because
         * that pair is the only one whose 4.5:1 thresholds meet across the
         * band. Four other jobs had been reading it: the selected category
         * pill, the announcement band, a button's ring and every focus
         * outline. All four painted raw black on a page whose colours all come
         * from the owner's photograph, and the owner reported three of them.
         *
         * `--shop-contrast` is a band that interrupts the page (the
         * announcement bar, the Apply button, a filled rail, the focus ring);
         * `--shop-selected` is "this control is on" — the same glass material
         * as every other pill, one measured step further out. Each declares
         * its own foreground, which is also what keeps muted text off them:
         * `mutedGrounds` excludes any ground that owns an `X-text`.
         * See `utils/coverTint.js`. */
        '--shop-contrast': fg.contrastBg,
        '--shop-contrast-text': fg.contrastInk,
        '--shop-selected': fg.selectedSolid,
        '--shop-selected-text': fg.selectedInk,
      };

      /* THE CHOSEN PILL WEARS THE SHOP'S ACCENT.
       *
       * Everything above solves surfaces against the GROUND, which is now
       * neutral on a classic page -- so a chosen pill solved the same way comes
       * out grey, and the shop loses the one place its colour was doing real
       * work. The owner's rule: the theme colour belongs on the things that
       * mean something (the selected pill, links, prices, buttons), not on the
       * furniture behind them.
       *
       * So the selected state is published from the accent itself rather than
       * from the ground. `readThemeAccent` reads --color-orange off the live
       * document, which is what `[data-theme]` remaps per shop, so this is the
       * business's own colour and not a guess. Its ink is chosen by luminance
       * against that accent -- a pale gold needs dark ink, a deep purple needs
       * white -- rather than assumed, because nine themes disagree about it.
       * If the accent cannot be read we leave the solved values alone. */
      const accent = readThemeAccent();
      if (accent) {
        const luma =
          (0.2126 * accent.r + 0.7152 * accent.g + 0.0722 * accent.b) / 255;
        vars['--shop-selected'] = `rgb(${accent.r}, ${accent.g}, ${accent.b})`;
        vars['--shop-selected-text'] = luma > 0.6 ? '#1B1B1F' : '#FFFFFF';
      }
      /* THE GROUND IS PUBLISHED WHICHEVER WAY IT WAS ARRIVED AT.
       *
       * `ground` is the derived cover tint when there is one and the
       * stylesheet's own ground read back when there is not, and by this line
       * it is the only colour in play — every token above was solved against
       * it. Writing it here is what makes "the page" a value rather than an
       * unresolved `color-mix()` that no measurement can reach, and it is
       * what stops `--shop-page-bg` (which several surfaces fade to) from
       * saying `#ffffff` on a page that is not white.
       *
       * It repaints nothing on the fallback path: `groundToTint` keeps full
       * precision and `tintToCssColor` round-trips it to the same bytes. */
      const groundCss = tintToCssColor(ground);
      vars['--shop-tint'] = groundCss;
      vars['--shop-page-bg'] = groundCss;
      // Applied last so it wins over the solved grounds above, and only over
      // the grounds — every legibility token stays as solved.
      if (classicGrounds) Object.assign(vars, classicGrounds);
      Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
      written = Object.keys(vars);
      /* WHICH WAY THE INK RUNS, AS A FACT RATHER THAN A COLOUR.
       *
       * A business's wordmark is a raster the owner drew — almost always dark
       * letterforms on transparent, for a light page. On a dark page (a FULL
       * shop with a dark cover) it disappears: dk-store's own header logo is
       * black on transparent and its page is deep red. The glyph colour this
       * hook solved above is exactly #000000 or #ffffff, so it already knows
       * which kind of page this is.
       *
       * Published as an ATTRIBUTE, not as another `--shop-*` token: it is not
       * a colour anything is painted in, and the contrast guards read every
       * published token as a ground to be measured. A stylesheet asks
       * `[data-shop-ink='light']` and turns a dark wordmark white there, while
       * a light page keeps the owner's own colours untouched. */
      root.setAttribute(INK_ATTR, String(fg.text).toLowerCase() === '#ffffff' ? 'light' : 'dark');
    };

    publish();

    // The ground can change under us without this hook's input changing: the
    // salon's theme lands, the owner's background mode resolves, or the
    // reader's system flips to dark. Each rewrites `--shop-tint` in CSS, and a
    // foreground published against the old one is the bug all over again.
    let observer = null;
    if (!tint && typeof MutationObserver === 'function') {
      observer = new MutationObserver(publish);
      observer.observe(root, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'data-shop-bg-mode'],
      });
    }

    return () => {
      if (observer) observer.disconnect();
      clear();
    };
  }, [tint]);
}
